/**
 * A place as stations (SA-1051 spec §7.3): every code the value names is a
 * `Station` — the lookup's row first, the stored words otherwise — with ` or `
 * between a choice's codes (a choice, of one candidate too, is always compact,
 * wherever it is mounted: the spec's strip rule, a full chip per candidate
 * would crowd a title, and a chip on a lone candidate would look resolved when
 * no one has picked it); a legacy string keeps its text as typed; a text or an
 * unknown code reads its words. The customer's alternatives (SA-1052) draw as
 * stations joined by ` or `, compact only when the caller asks: each is a
 * station the customer named. The caller holds the lookup.
 */
import { Fragment } from 'react'
import type { Airport } from '@/types/airports'
import { OR, entryStationWords, isPlace, placeCodes, placeStationWords, placeState, placeWords, type StationWords } from './place'
import { airportFor } from './airportFor'
import { Station } from './Station'

/** The words the value itself stores for a code, for when the lookup has no row. */
function wordsFor(value: unknown, code: string): StationWords | null {
  if (!isPlace(value)) return null
  if (value.resolved && value.code === code) return placeStationWords(value)
  const stored = [...(Array.isArray(value.alternatives) ? value.alternatives : []), ...(Array.isArray(value.candidates) ? value.candidates : [])]
  const c = stored.find((x) => x && x.code === code)
  return c ? entryStationWords(c) : null
}

export function PlaceStations({ value, airports, compact = false }: { value: unknown; airports: Record<string, Airport>; compact?: boolean }) {
  if (typeof value === 'string') {
    return <Station text={value} airport={airportFor(airports, value)} compact={compact} />
  }
  const codes = placeCodes(value)
  if (codes.length === 0) return <>{placeWords(value)}</>
  const compactAll = compact || (isPlace(value) && placeState(value) === 'choice')
  return (
    <>
      {codes.map((code, i) => (
        <Fragment key={code}>
          {i > 0 && OR}
          <Station text={code} airport={airports[code] ?? wordsFor(value, code)} compact={compactAll} />
        </Fragment>
      ))}
    </>
  )
}
